import type { NextPage } from 'next';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import React from 'react';
import styled from '@emotion/styled';

import { ThemeProvider, Global } from '@emotion/react';
import { Toaster, DEFAULT_THEME, IconLoading, global } from '@myrealtrip/web-ui';
import { HydrationBoundary, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { WishProvider } from '@myrealtrip/wish';

import ErrorBoundary from '~components/ErrorBoundary';
import { Auth } from '~components/auth';
import AppMeta from '~components/metas/AppMeta';
import { MarketingAutoOpen } from '@myrealtrip/marketing-log';
import { AuthProvider } from '~contexts/AuthProvider';
import { DetermineMobileProvider, WebViewProvider } from '@myrealtrip/device-detect';
import ExpireProvider from '~contexts/ExpireContext';
import ServerSideQueryProvider from '~contexts/ServerSideQueryProvider';
import globalStyle from '~styles/globals';

type EnhancedAppProps = AppProps & {
  Component: NextPage;
  pageProps: Record<string, unknown>;
};

const isProduction = process.env.NODE_ENV === 'production';

function CustomApp({ Component, pageProps }: EnhancedAppProps) {
  const getLayout = Component.getLayout ?? ((page) => page);
  const { isMobile, webViewInfo, ...restPageProps } = pageProps;
  const { queries } = restPageProps;

  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 15000,
            refetchOnWindowFocus: false,
            refetchOnReconnect: isProduction,
            retry: false,
            networkMode: 'always',
          },
        },
      }),
  );
  const router = useRouter();
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    /** Mount 이후 Loading 제외 */
    setLoading(false);
  }, []);

  React.useEffect(() => {
    const handleStart = (url: string) => {
      const changeLocation = new URL(`${window.location.origin}${url}`);
      setLoading(window.location.pathname !== changeLocation.pathname);
    };
    const handleComplete = () => setLoading(false);
    const handleError = () => setLoading(false);

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleError);

    // popstate: 브라우저 히스토리 이동 대응
    const handlePopState = () => setLoading(false);
    window.addEventListener('popstate', handlePopState);

    // visibilitychange: 웹뷰 닫힘 등 대응
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setLoading(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup event listeners
    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.off('routeChangeError', handleError);
    };
  }, [router]);

  return (
    <>
      <AppMeta />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover"
      />
      <QueryClientProvider client={queryClient}>
        {process.env.NODE_ENV === 'development' && (
          <DevtoolsContainer>
            <ReactQueryDevtools buttonPosition="bottom-left" />
          </DevtoolsContainer>
        )}
        <HydrationBoundary state={pageProps.dehydratedState}>
          <ServerSideQueryProvider queries={queries}>
            <WebViewProvider defaultWebViewInfo={webViewInfo}>
              <AuthProvider>
                <ThemeProvider theme={DEFAULT_THEME}>
                  <ExpireProvider>
                    <WishProvider hideToast>
                      <Global styles={[global, globalStyle]} />
                      <Toaster />
                      {loading && (
                        <IconLoading
                          isFullScreen
                          preventScrollProps={{ ignoreIosScroll: true, ignoreScrollBarArea: true }}
                        />
                      )}
                      <DetermineMobileProvider defaultMobile={Boolean(isMobile)}>
                        <MarketingAutoOpen />
                        <ErrorBoundary>
                          {getLayout(
                            Component.auth ? (
                              <Auth config={Component.auth}>
                                <Component {...restPageProps} />
                              </Auth>
                            ) : (
                              <Component {...restPageProps} />
                            ),
                          )}
                        </ErrorBoundary>
                      </DetermineMobileProvider>
                    </WishProvider>
                  </ExpireProvider>
                </ThemeProvider>
              </AuthProvider>
            </WebViewProvider>
          </ServerSideQueryProvider>
        </HydrationBoundary>
      </QueryClientProvider>
    </>
  );
}

export default CustomApp;

const DevtoolsContainer = styled.div`
  .tsqd-main-panel {
    z-index: 99999999999999;
  }
`;
